import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconCheck, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import {
  quoteCreateSchema,
  VAT_RATE_OPTIONS,
  vatRateLabel,
  type QuoteCreateInput,
  type QuoteDto,
  type QuoteStatus,
  type ServiceCatalogItemDto,
} from '@expeditor/shared';
import { quotesApi } from '../../shared/api/quotes';
import { serviceCatalogApi } from '../../shared/api/service-catalog';
import { DateField } from '../../shared/ui/DateField';
import { useAuthStore } from '../../shared/auth/auth.store';

interface LineForm {
  code?: string;
  name: string;
  amountNet: number | string;
  vatRate: string;
}
const fmt = (n: number) => n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_COLOR: Record<QuoteStatus, string> = {
  PROPOSED: 'blue',
  ACCEPTED: 'green',
  REJECTED: 'red',
  EXPIRED: 'gray',
  REVOKED: 'gray',
};

const STATUS_LABEL: Record<QuoteStatus, string> = {
  PROPOSED: 'Предложена',
  ACCEPTED: 'Принята',
  REJECTED: 'Отклонена',
  EXPIRED: 'Истекла',
  REVOKED: 'Отозвана',
};

interface Props {
  orderId: string;
  /** Поведение/доступные действия зависят от роли. */
  viewerRole: 'client' | 'manager';
  /** Статус Order — чтобы скрыть форму создания, если не SUBMITTED/QUOTED. */
  orderStatus: string;
  /** Услуги, заказанные клиентом в поручении — предзаполняют котировку. */
  orderServices?: { code: string; name: string | null }[];
  /** Колбэк после accept/reject/create — родитель может перезагрузить order. */
  onChange?: () => void;
}

/**
 * Секция котировок на карточке поручения.
 *
 * - Клиент видит свои котировки, может Accept / Reject PROPOSED-котировки.
 * - Менеджер видит все котировки к поручению, может выставить новую (если
 *   статус Order — SUBMITTED/QUOTED) и Revoke свои PROPOSED.
 */
export function OrderQuotesSection({
  orderId,
  viewerRole,
  orderStatus,
  orderServices,
  onChange,
}: Props) {
  const me = useAuthStore((s) => s.user);
  const [items, setItems] = useState<QuoteDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  // create form state — котировка как список позиций (услуг)
  const [lines, setLines] = useState<LineForm[]>([]);
  const [currency, setCurrency] = useState('RUB');
  const [validUntil, setValidUntil] = useState('');
  const [workScope, setWorkScope] = useState('');
  const [serviceCatalog, setServiceCatalog] = useState<ServiceCatalogItemDto[]>([]);

  const totalNet = lines.reduce((s, l) => s + (Number(l.amountNet) || 0), 0);
  const totalVat = lines.reduce(
    (s, l) => s + ((Number(l.amountNet) || 0) * (Number(l.vatRate) || 0)) / 100,
    0,
  );
  const lineUpdate = (i: number, patch: Partial<LineForm>) =>
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const lineRemove = (i: number) => setLines(lines.filter((_, idx) => idx !== i));
  const addCatalogService = (code: string) => {
    const s = serviceCatalog.find((x) => x.code === code);
    if (s) setLines([...lines, { code: s.code, name: s.name, amountNet: '', vatRate: '20' }]);
  };
  const addBlankLine = () => setLines([...lines, { name: '', amountNet: '', vatRate: '20' }]);

  const openCreate = () => {
    if (lines.length === 0) {
      setLines(
        (orderServices ?? []).map((s) => ({
          code: s.code,
          name: s.name ?? s.code,
          amountNet: '',
          vatRate: '20',
        })),
      );
    }
    // Срок действия по умолчанию — +7 дней (видим и редактируем, не «появляется» сам).
    if (!validUntil) {
      const d = new Date(Date.now() + 7 * 86400_000);
      const p = (n: number) => String(n).padStart(2, '0');
      setValidUntil(`${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`);
    }
    setCreating(true);
  };

  const reload = async () => {
    try {
      const data = await quotesApi.listForOrder(orderId);
      setItems(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить котировки');
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (viewerRole === 'manager') {
      serviceCatalogApi.listActive().then(setServiceCatalog).catch(() => {});
    }
  }, [viewerRole]);

  const onAccept = async (id: string) => {
    if (!confirm('Принять котировку? Это создаст договор автоматически.')) return;
    setBusy(true);
    try {
      await quotesApi.accept(id);
      await reload();
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось принять');
    } finally {
      setBusy(false);
    }
  };

  const onReject = async (id: string) => {
    if (!confirm('Отклонить котировку?')) return;
    setBusy(true);
    try {
      await quotesApi.reject(id);
      await reload();
      onChange?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отклонить');
    } finally {
      setBusy(false);
    }
  };

  const onRevoke = async (id: string) => {
    if (!confirm('Отозвать котировку?')) return;
    setBusy(true);
    try {
      await quotesApi.revoke(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось отозвать');
    } finally {
      setBusy(false);
    }
  };

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validLines = lines.filter((l) => l.name.trim() && l.amountNet !== '');
    if (validLines.length === 0) {
      setError('Добавьте хотя бы одну услугу с ценой');
      return;
    }
    const dto: QuoteCreateInput = {
      currency: currency.toUpperCase(),
      lines: validLines.map((l) => ({
        code: l.code || undefined,
        name: l.name.trim(),
        amountNet: Number(l.amountNet),
        vatRate: Number(l.vatRate || '0'),
      })),
      validUntil: validUntil
        ? new Date(validUntil + 'T23:59:59').toISOString()
        : new Date(Date.now() + 7 * 86400_000).toISOString(),
      workScope: workScope || undefined,
    };
    const parsed = quoteCreateSchema.safeParse(dto);
    if (!parsed.success) {
      setError(parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '));
      return;
    }
    setBusy(true);
    try {
      await quotesApi.createForOrder(orderId, parsed.data);
      setCreating(false);
      setLines([]);
      setWorkScope('');
      setValidUntil('');
      await reload();
      onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось выставить котировку');
    } finally {
      setBusy(false);
    }
  };

  const canManagerCreate =
    viewerRole === 'manager' && (orderStatus === 'SUBMITTED' || orderStatus === 'QUOTED');

  return (
    <Card withBorder>
      <Group justify="space-between" mb="sm">
        <Title order={4}>Котировки</Title>
        {canManagerCreate && (
          <Button leftSection={<IconPlus size={16} />} onClick={openCreate} size="xs">
            Выставить котировку
          </Button>
        )}
      </Group>

      {error && (
        <Alert color="red" mb="sm">
          {error}
        </Alert>
      )}

      {items.length === 0 ? (
        <Text c="dimmed" size="sm">
          Котировок пока нет.
        </Text>
      ) : (
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Сумма</Table.Th>
              <Table.Th>Срок</Table.Th>
              <Table.Th>Состав работ</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th style={{ width: 130 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((q) => {
              const isAuthor = viewerRole === 'manager' && me?.userId === q.managerId;
              return (
                <Table.Tr key={q.id}>
                  <Table.Td>
                    <Text fw={500}>
                      {fmt(q.amount)} {q.currency}
                    </Text>
                    {q.amountNet != null && q.vatAmount != null && (
                      <Text size="xs" c="dimmed">
                        без НДС {fmt(q.amountNet)} · НДС {fmt(q.vatAmount)}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>{new Date(q.validUntil).toLocaleDateString('ru-RU')}</Table.Td>
                  <Table.Td>
                    {q.lines && q.lines.length > 0 ? (
                      <Stack gap={2}>
                        <Stack gap={0}>
                          {q.lines.map((l, i) => (
                            <Text key={i} size="xs">
                              {l.name} — {fmt(l.amountNet)} {q.currency} ({vatRateLabel(l.vatRate)})
                            </Text>
                          ))}
                        </Stack>
                        {q.workScope &&
                          q.workScope !== q.lines.map((l) => l.name).join(', ') && (
                            <Text size="xs" c="dimmed">
                              Примечание: {q.workScope}
                            </Text>
                          )}
                      </Stack>
                    ) : (
                      <Text size="sm" lineClamp={2}>
                        {q.workScope}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[q.status]}>{STATUS_LABEL[q.status]}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {q.status === 'PROPOSED' && viewerRole === 'client' && (
                      <Group gap={4}>
                        <Button
                          size="xs"
                          color="green"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => onAccept(q.id)}
                          loading={busy}
                        >
                          Принять
                        </Button>
                        <Button
                          size="xs"
                          color="red"
                          variant="default"
                          leftSection={<IconX size={14} />}
                          onClick={() => onReject(q.id)}
                          loading={busy}
                        >
                          Отклонить
                        </Button>
                      </Group>
                    )}
                    {q.status === 'PROPOSED' && isAuthor && (
                      <Button
                        size="xs"
                        variant="default"
                        onClick={() => onRevoke(q.id)}
                        loading={busy}
                      >
                        Отозвать
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={creating} onClose={() => setCreating(false)} title="Новая котировка" size="lg">
        <form onSubmit={onCreate}>
          <Stack>
            <Text size="sm" fw={500}>
              Услуги
            </Text>
            {lines.length === 0 && (
              <Text size="xs" c="dimmed">
                Добавьте услуги ниже (из справочника или свою).
              </Text>
            )}
            {lines.map((l, i) => (
              <Group key={i} align="flex-end" gap="xs" wrap="nowrap">
                <TextInput
                  label={i === 0 ? 'Услуга' : undefined}
                  value={l.name}
                  onChange={(e) => lineUpdate(i, { name: e.currentTarget.value })}
                  style={{ flex: 1 }}
                />
                <NumberInput
                  label={i === 0 ? 'Цена без НДС' : undefined}
                  value={l.amountNet}
                  onChange={(v) => lineUpdate(i, { amountNet: v })}
                  min={0}
                  decimalScale={2}
                  thousandSeparator=" "
                  w={140}
                />
                <Select
                  label={i === 0 ? 'НДС' : undefined}
                  data={VAT_RATE_OPTIONS}
                  value={l.vatRate}
                  onChange={(v) => lineUpdate(i, { vatRate: v ?? '0' })}
                  w={100}
                  allowDeselect={false}
                />
                <ActionIcon color="red" variant="subtle" mb={6} onClick={() => lineRemove(i)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
            <Group gap="xs">
              <Select
                placeholder="Добавить услугу из справочника"
                data={serviceCatalog.map((s) => ({ value: s.code, label: s.name }))}
                value={null}
                onChange={(v) => v && addCatalogService(v)}
                searchable
                clearable
                style={{ flex: 1 }}
              />
              <Button
                variant="default"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={addBlankLine}
              >
                Своя услуга
              </Button>
            </Group>
            <Group justify="flex-end" gap="lg">
              <Text size="sm" c="dimmed">
                Без НДС: {fmt(totalNet)}
              </Text>
              <Text size="sm" c="dimmed">
                НДС: {fmt(totalVat)}
              </Text>
              <Text fw={600}>
                Итого: {fmt(totalNet + totalVat)} {currency}
              </Text>
            </Group>
            <Group grow>
              <TextInput
                label="Валюта (ISO 4217)"
                required
                value={currency}
                onChange={(e) => setCurrency(e.currentTarget.value)}
                maxLength={3}
              />
              <DateField label="Срок действия" value={validUntil} onChange={setValidUntil} />
            </Group>
            <Textarea
              label="Примечание (необязательно)"
              minRows={2}
              value={workScope}
              onChange={(e) => setWorkScope(e.currentTarget.value)}
              placeholder="Доп. условия, комментарий к котировке..."
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreating(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={busy}>
                Выставить
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Card>
  );
}
